import React, { useEffect, useMemo, useState } from 'react';

import Modal from './Modal';
import {
  buildMoveSwapConfirmationCopy,
  buildMoveSwapDayOptions,
  getMoveSwapOptionLabel,
} from '../utils/calendarMoveSwap';
import { useLocale } from '../context/LocaleContext';

const QuestMoveSwapModal = ({
  isOpen,
  onClose,
  onConfirm,
  quest,
  allQuests,
  timezone,
  errorMessage = '',
  isSubmitting = false,
}) => {
  const { locale, t } = useLocale();
  const [selectedDateKey, setSelectedDateKey] = useState('');
  const [step, setStep] = useState('select');

  const options = useMemo(() => buildMoveSwapDayOptions({
    quest,
    allQuests,
    timezone,
    locale,
  }), [allQuests, locale, quest, timezone]);

  const selectedOption = useMemo(
    () => options.find((option) => option.dateKey === selectedDateKey) || null,
    [options, selectedDateKey]
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedDateKey('');
      setStep('select');
      return;
    }

    const firstEnabled = options.find((option) => option.enabled);
    setSelectedDateKey(firstEnabled?.dateKey || '');
    setStep('select');
  }, [isOpen, quest]);

  useEffect(() => {
    if (!isOpen || isSubmitting) {
      return;
    }
    if (selectedDateKey && options.some((option) => option.dateKey === selectedDateKey)) {
      return;
    }
    const firstEnabled = options.find((option) => option.enabled);
    setSelectedDateKey(firstEnabled?.dateKey || '');
  }, [isOpen, isSubmitting, options, selectedDateKey]);

  useEffect(() => {
    if (!errorMessage) {
      return;
    }
    setStep('confirm');
  }, [errorMessage]);

  const handleContinue = () => {
    if (!selectedOption?.enabled) {
      return;
    }
    setStep('confirm');
  };

  const handleBack = () => {
    if (isSubmitting) {
      return;
    }
    setStep('select');
  };

  const handleConfirm = () => {
    if (!selectedOption?.enabled || isSubmitting) {
      return;
    }
    onConfirm(selectedOption);
  };

  const resolveReasonLabel = (reason) => {
    const mapping = {
      'Current day': 'moveSwap.reasons.currentDay',
      'Completed quest': 'moveSwap.reasons.completedQuest',
      'Completed workout': 'moveSwap.reasons.completedWorkout',
      'Earlier empty day': 'moveSwap.reasons.earlierEmptyDay',
      'Unavailable': 'moveSwap.reasons.unavailable',
      'No mindfulness quest': 'moveSwap.reasons.noMindfulnessQuest',
    };

    if (mapping[reason]) {
      return t(mapping[reason], reason);
    }

    const noQuestMatch = /^No .+ quest$/i.exec(reason || '');
    if (noQuestMatch) {
      return t('moveSwap.reasons.noMatchingQuest', 'No matching quest');
    }

    return reason;
  };

  return (
    <Modal isOpen={isOpen} onClose={isSubmitting ? () => {} : onClose} title={t('moveSwap.title', 'Move / Swap')} size="md">
      <div className="space-y-4">
        <p className="text-sm text-slate-300">
          {t('moveSwap.description', 'Change when this quest happens this week.')}
        </p>

        {errorMessage ? (
          <div className="rounded-2xl border border-red-500/30 bg-red-950/40 px-4 py-3 text-sm font-semibold text-red-100">
            {errorMessage}
          </div>
        ) : null}

        {step === 'select' ? (
          <>
            <div className="space-y-2">
              {options.map((option) => {
                const selected = option.dateKey === selectedDateKey;
                return (
                  <button
                    key={option.dateKey}
                    type="button"
                    disabled={!option.enabled}
                    onClick={() => setSelectedDateKey(option.dateKey)}
                    className={[
                      'w-full rounded-2xl border px-4 py-3 text-left transition-colors',
                      selected ? 'border-cyan-400/60 bg-cyan-500/10' : 'border-white/10 bg-slate-950/50',
                      option.enabled ? 'hover:border-cyan-400/40 hover:bg-slate-900/70' : 'cursor-not-allowed opacity-65',
                    ].join(' ')}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-white">{option.shortLabel}</p>
                        <p className="mt-1 text-xs text-slate-400">
                          {option.enabled
                            ? getMoveSwapOptionLabel(option, t)
                            : resolveReasonLabel(option.reason)}
                        </p>
                      </div>
                      <span
                        className={[
                          'rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em]',
                          option.enabled
                            ? 'bg-cyan-500/15 text-cyan-200'
                            : 'bg-slate-800/80 text-slate-400',
                        ].join(' ')}
                      >
                        {option.enabled
                          ? t(`moveSwap.actions.${option.action}`, option.action)
                          : t('moveSwap.unavailable', 'Unavailable')}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-white/15 bg-slate-900/70 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-slate-800"
              >
                {t('moveSwap.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                disabled={!selectedOption?.enabled}
                onClick={handleContinue}
                className="rounded-xl bg-cyan-500 px-4 py-2 text-sm font-bold text-slate-950 transition-colors hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('moveSwap.continue', 'Continue')}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-4">
              <p className="text-sm font-semibold text-white">
                {buildMoveSwapConfirmationCopy(selectedOption, t)}
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleBack}
                disabled={isSubmitting}
                className="rounded-xl border border-white/15 bg-slate-900/70 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('moveSwap.back', 'Back')}
              </button>
              <button
                type="button"
                disabled={!selectedOption?.enabled || isSubmitting}
                onClick={handleConfirm}
                className="rounded-xl bg-cyan-500 px-4 py-2 text-sm font-bold text-slate-950 transition-colors hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? t('moveSwap.saving', 'Saving...') : t('moveSwap.confirm', 'Confirm')}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default QuestMoveSwapModal;
